import { Component, OnInit } from '@angular/core';
import { SecurityService } from '../Security/security.service';
import { LogService } from '../Abstract/log.service';
import { GridService } from '../Abstract/grid.service';
import { VisualsService } from '../Abstract/visuals.service';
import { Observable, throwError } from 'rxjs';
import { ConfigService } from '../Abstract/config.service';
import { ColumnSetting } from '../../Core/Abstract/Grid/layout.model';
import { FieldConfig } from '../../dynamic-form/models/field-config.interface';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'base-grid',
    template: ''
})

export abstract class BaseGridComponent <T extends GridService> {
    protected group;
    
    public groupToService(group:string){
        //get mapping of group -> service from localstorage (filled in login)
        let groupMap = this.securityService.getFromStorageAsJSON('groupServiceMap');
        if(groupMap && groupMap[group]){
            return groupMap[group];
        }
        return group.substr(0,1).toUpperCase() + group.slice(1) + 'Service';
    }

    //Search Form
    protected continueWithSearch:boolean = true;    
    protected form;
    protected formErrors = {};
    protected validationMessages= {};

    //Form input pattern regexes
    protected dateRegex = /(^(((0[1-9]|1[0-9]|2[0-8])[\/](0[1-9]|1[012]))|((29|30|31)[\/](0[13578]|1[02]))|((29|30)[\/](0[4,6,9]|11)))[\/](19|[2-9][0-9])\d\d$)|(^29[\/]02[\/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/;
    protected intRegex = /(^0$)|(^[1-9]\d*$)/;
    protected decimalRegex = /^[0-9]+(\.[0-9]{1,2})?$/;

    //Grid
    protected count:number = 0;
    protected cookieId;    
    protected grid;
    protected footer = {};
    protected header = {};
    protected gridActions = [];
    protected hasRowCheckbox:boolean = false;
    public checkboxPK = null;
    public checkboxPKArrayFull:Array<number> = [];
    public checkboxPKArray:Array<number> = [];
    protected columnSetting: ColumnSetting[] = [];
    
    //Sort
    protected sort:string = '1';
    protected sortNum:string = '1';
    protected sortDir:string = 'asc';

    //Pagination
    protected pageSize:number = 20;
    protected pageNum:number = 1; 
    protected pageMax:number = 0;
    protected pageArray:Array<number> = [];


    //Visuals
    protected visuals:any = {};
    protected genericVisuals:any = {};

    //UI handlers
    protected searched:boolean = false;
    protected loading:boolean = false;
    protected downloading:boolean = false;
    protected checkboxLoading:boolean = false;

    constructor(protected securityService: SecurityService,protected logger: LogService,
                protected visualsService: VisualsService,protected gridService:T, 
                group?:string){

        this.group = group;
        this.pageSize = this.securityService.getPageSize();
    }

    /**
     ** Grid Utils
     **/

    //URLparams sanitization
    protected sanitize(data: any){
        return data != null ? data : '';
    }

    //Grid Action function handler
    protected outputMethodHandler(params:any){
        let methodName = params.methodName;
        if(this[methodName]) {
            //function exists on the component
            this[methodName](params); // call it
            
        }
    }

    /**
     * Visuals
     */
    protected loadVisuals():void{
        this.getGenericVisuals();
        this.getVisuals();
    }

    protected getGenericVisuals(){
        this.genericVisuals = this.visualsService.getFromStorageAsJSON('visualsArray') ?
        this.visualsService.getFromStorageAsJSON('visualsArray')['generic'] : {};
    }

    protected getVisuals(){
        this.visuals = this.visualsService.getFromStorageAsJSON('visualsArray') ?
        this.visualsService.getFromStorageAsJSON('visualsArray')[this.group] : {};
    }

    /**
     ** Grid Search Form Functions
     **/

    /**
     * @argument formConfig:FieldConfig as emitted by @see DynamicFormComponent @function ngOnInit()
     */
    afterFormInit(formConfig:any){
         //implement to autofill/handle form values
    }

    handleFormChanges(formConfig: any) {
        if (!this.form) { return; }
        this.form.changes.subscribe(data => this.onValueChanged(data));
        this.getValidationMessages();
        this.afterFormInit(formConfig);
    }
    
    protected getValidationMessages() {
        if (!this.form) { return; }
        let controls = this.form['controls'] ? this.form['controls'] : [];
        controls.forEach(field => {
            this.formErrors[field.name] = '';
            this.validationMessages[field.name] = {};
            if(field.validation){
                let validationAr:Array<string> = (field.validation).split(',');
                validationAr.forEach(rule => {
                    if (rule === 'R') {
                        this.validationMessages[field.name]['required'] = this.genericVisuals.required;
                    } else if (rule === 'D') {
                        this.validationMessages[field.name]['pattern'] = this.genericVisuals.dateerror;
                    } else if (rule === 'I') {
                        this.validationMessages[field.name]['pattern'] = this.genericVisuals.interror;
                    } 
                });
            }
        });
        this.onValueChanged();        
    };

    onValueChanged(data?: any) {
        if (!this.form) { return; }
        const form = this.form.form;
        this.continueWithSearch = true;
        // this.logger.log(form);
        for (const field in this.formErrors) {
            // this.logger.log(field);
            // clear previous error message (if any)
            this.formErrors[field] = '';
            const control = form.get(field);

            if (control && control.dirty && !control.valid) {
                const messages = this.validationMessages[field];
                // this.logger.log(control.errors)
                for (const key in control.errors) {
                    this.formErrors[field] += messages[key] + ' ';
                }
                this.continueWithSearch = false;
            }
        }
        // this.logger.log("formerrors: " + JSON.stringify(this.formErrors));
    }

    submit(value: {[name: string]: any}) {
        Object.keys(value).forEach(val => {
            if(Array.isArray(value[val])){
                //get value of ngx-select
                let selector = value[val][0];
                if (selector!=null && selector.hasOwnProperty('id')){
                    value[val] = this.sanitize(value[val][0]['id']);
                } else {
                    value[val] = "";
                }
            } else {
                value[val] = this.sanitize(value[val]);
            }
        });

        this.logger.log(value);

        this.initParams = value;
        this.search();
    }

    protected search(): void{
        this.loading = true;
        this.pageNum = 1; 
        this.getInitData();
    }


    /**
     ** All standard Grid functions
     **/


    /**
     *  Grid Data serialized call hierarchy
     */ 

    /**
     * can be implemented to handle data returned by
     * @see getData()
     */
    protected getDataCallback(){}

    protected initParams = {};    
    protected initDataParams(): any {
        //autofilled by submit(...)
        return this.initParams;
    }
    protected getInitData(): void {        
        if (this.continueWithSearch) {
            this.loading = true;
            this.searched = true;
            this.gridService.initData(this.initDataParams()).subscribe(
                data => {
                    this.cookieId = data;
                    this.logger.log(this.cookieId);
                    this.getColumnDefinition();
                    this.getGridActions();
                },
                error => {
                    this.loading = false;
                    return throwError(error);
                }
            );
        }
    }

    protected gridActionsParams(): any {
        return {
            "group" : this.group
        }
    }
    protected getGridActions(): void {
        
        this.gridService.getGridActions(this.gridActionsParams()).subscribe(
            data => {
                if(data)
                    this.gridActions = data;
                
                if(this.gridActions){
                    this.gridActions.forEach(element => {
                        if(element['method'] == 'selectAll'){
                            this.hasRowCheckbox = element['hidden'] ? element['hidden'] :  false;
                        }
                    });                   
                }
            },
            error => {
                this.loading = false;
                return throwError(error);
            }
        );
    }

    protected columnDefinitionParams(): any {
        return {
            "group" : this.group
        }
    }
    protected getColumnDefinition(): void {
        
        this.gridService.getColumnDefinition(this.columnDefinitionParams()).subscribe(
            data => {
                this.columnSetting = data;
                this.getDataCount();
            },
            error => {
                this.loading = false;
                return throwError(error);
            }
        );
    }

    protected dataCountParams(): any {
        return {
            "cookieId" : this.cookieId
        }
    }
    protected getDataCount(): void {
        this.gridService.getDataCount(this.dataCountParams()).subscribe(
            data => {
                this.count = data;
                if (this.count > 0) {
                    this.calcPages();
                    this.getHeaderData();
                    this.getFooterData();                    
                    this.getData();
                } else {
                    this.loading = false;
                }
            },
            error => {
                this.loading = false;
                this.count = 0;
                return throwError(error);
            }
        );
    }

    protected dataParams(): any {
        let firstRow = 1 + this.pageSize * (this.pageNum - 1);
        let lastRow = firstRow + this.pageSize - 1;

        return {
            "cookieId" : this.cookieId,
            "start" : firstRow,
            "end" : lastRow,
            "order" : this.sort
        }
    }
    protected getData(): void {
        this.gridService.getData(this.dataParams()).subscribe(
            data => {
                this.grid = data;
                this.getDataCallback();
                this.loading = false;
                this.checkboxLoading = false;
            },
            error => {
                this.loading = false;
                this.checkboxLoading = false;
                return throwError(error);
            }
        );
    }

    protected footerDataParams(): any {
        return {
            "cookieId" : this.cookieId
        }
    }
    protected getFooterData(): void {
        this.gridService.getFooter(this.footerDataParams()).subscribe(
            data => {
                if(data)
                    this.footer = data;
            },
            error => {
                return throwError(error);
            }
        );
    }

    protected headerDataParams(): any {
        return {
            "cookieId" : this.cookieId
        }
    }
    protected getHeaderData(): void {
        this.gridService.getHeader(this.headerDataParams()).subscribe(
            data => {
                if(data)
                    this.header = data;
            },
            error => {
                return throwError(error);
            }
        );
    }


    protected txtParams(): any {
        return {
            "cookieId" : this.cookieId,
            "order" : this.sort            
        }
    }
    protected getTxt() {
        this.downloading = true;
        this.gridService.getTxt(this.txtParams()).subscribe(
            data => {
                this.logger.log("downloading the file");
                this.downloading = false;
            },

            error => {
                this.logger.error(error);
                this.downloading = false;
            }
        );
    }

    protected pdfParams(): any {
        return {
            "cookieId" : this.cookieId,
            "order" : this.sort            
        }
    }
    protected getPdf() {
        this.downloading = true;
        this.gridService.getPdf(this.pdfParams()).subscribe(
            data => {
                this.logger.log("downloading the file");
                this.downloading = false;
            },

            error => {
                this.logger.error(error);
                this.downloading = false;
            }
        );
    }

    /**
     * Grid Sorting
     */
    public changeSort(num:any): void{
        /*
        let sortNum;
        if (!(typeof num === 'number' && (num%1) === 0) && num.toString().match(/^[1-9]\d*$/)){
            sortNum=  Number(num);
        }else if(typeof num === 'number' && (num%1) === 0){
            sortNum = num
        }
        */
        this.loading = true;
        if (this.sortNum == num && this.sortDir == 'desc') {
            this.sortDir = 'asc';
        } else if (this.sortNum == num && this.sortDir == 'asc') {
            this.sortDir = 'desc';
        }  else {
            this.sortNum = num;
            this.sortDir = 'asc';
        }

        this.sort = this.sortNum + ' ' +this.sortDir;
        this.getDataCount();

    }

    protected getSortState(column){
        return (this.sortNum == column) ? (this.sortDir == 'asc' ? 'sortasc': 'sortdesc' ): 'unsorted' ;
    }

    /**
     * Grid Pagination
     */
    public changePageSize(num:any): void{
        this.loading = true;
        this.pageSize = num;
        this.pageNum = 1;
        this.getDataCount();
    }

    public changePage(obj:any): void{
        let num = obj['num'];
        let type = obj['type'];

        if (!(typeof num === 'number' && (num%1) === 0) && num.toString().match(/^[1-9]\d*$/)){
            this.loading = true;
            let number = Number(num);
            this.changePageType(number,type);
            this.getDataCount();
        }else if(typeof num === 'number' && (num%1) === 0){
            this.loading = true;
            this.changePageType(num,type);
            this.getDataCount();
        }
    }

    public changePageType(num:number, type:string): void{
        if(type == 'full'){
            if(num>this.pageMax){
                this.pageNum = this.pageMax;
            }else{
                this.pageNum = num;
            }  
        } else if(type == 'next') {
            this.pageNum = num+1;
        }
        else if(type == 'prev') {
            this.pageNum = num-1;
        }
    }

    public calcPages(): void{
        this.pageMax = Math.ceil(this.count / this.pageSize) ;
        for (let i = 0; i < this.pageMax; i++) {
            this.pageArray.push(i + 1)
        }
    }

    
    /**
     * Grid checkboxes
     */
        
    public getCheckboxCallback(){}

    public dataCheckboxInit() {}

    public filterCheckboxParams(params): any {
        if(this.checkboxPK){
            let PK = {};
            if(Array.isArray(this.checkboxPK)){
                this.checkboxPK.forEach(element => {
                    PK[element] = params[element];
                });
            } else if(typeof this.checkboxPK === 'string') {
                PK[this.checkboxPK] = params[this.checkboxPK];
            } else {
                return params;
            }
            return PK;
        }else{
            return params;
        }
    }

    /**
     * Grid checkbox toggling
     */
    protected checkboxToggleParams(): any {
        //passes whole (row)Object:any by default, lets server handle used params(PK)
        //override any of its properties here
        return {
            "cookieId" : this.cookieId
        }
    }
    protected checkToggle(params) {
        this.checkboxLoading = true;
        this.searched = true;
        params.columnMaps = undefined;
        if(params['selected'] != null && params['selected'] != '') {
            if(params['selected'] == 'Y'){

                this.gridService.deselect( Object.assign({},params,this.checkboxToggleParams()) ).subscribe(
                    data => {
                        this.logger.log('deselect ' + data);
                        this.getData();
                    },
                    error => {
                        this.checkboxLoading = false;
                        return throwError(error);
                    }
                );

            } else {

                this.gridService.select( Object.assign({},params,this.checkboxToggleParams()) ).subscribe(
                    data => {
                        this.logger.log('select ' + data);
                        this.getData();
                    },
                    error => {
                        this.checkboxLoading = false;
                        return throwError(error);
                    }
                );

            }
        } else {
            this.logger.error('Object property "selected" missing.');
        }


    } 

    protected checkboxAllParams(): any {
        return {
            "cookieId" : this.cookieId
        }
    }
    protected selectAll(params) {
        this.checkboxLoading = true;
        this.searched = true;
        this.gridService.selectAll(this.checkboxAllParams()).subscribe(
            data => {
                this.logger.log('selectAll ' + data);
                this.getData();
            },
            error => {
                this.checkboxLoading = false;
                return throwError(error);
            }
        );
    }

    protected deselectAll(params) {
        this.checkboxLoading = true;
        this.searched = true;
        this.gridService.deselectAll(this.checkboxAllParams()).subscribe(
            data => {
                this.logger.log('deselectAll ' + data);
                this.getData();
            },
            error => {
                this.checkboxLoading = false;
                return throwError(error);
            }
        );
    }
}